import {Texture, Vector2, Vector3, WebGLRenderer, WebGLRenderTarget} from "three";
import {TextureHelper} from "features/apartmentViewer/ApartmentViewer/helpers/TextureHelper";

export class DepthManager {

    width: number = 0;
    height: number = 0;
    depth: Float32Array = new Float32Array();

    static DEFAULT_WIDTH = 512;
    static DEFAULT_HEIGHT = 256;
    static HORIZONTAL_OFFSET = 0.25;

    constructor(private textureHelper: TextureHelper, private renderer: WebGLRenderer) {}

    setupDepth(texture: Texture, width: number = DepthManager.DEFAULT_WIDTH, height: number = DepthManager.DEFAULT_HEIGHT): void {
        if (width <= 0 || height <= 0) {
            throw new Error("Invalid width or height provided for setupDepthTexture. Must be greater than 0.");
        }

        this.width = width;
        this.height = height;

        const scaledTexture = this.scaleTextureToDesiredSize(texture, width, height);
        const pixelBuffer = this.readTexturePixels(scaledTexture);
        this.initializeDepthArray(pixelBuffer);

        scaledTexture.dispose();
    }

    private scaleTextureToDesiredSize(texture: Texture, width: number, height: number): WebGLRenderTarget {
        return this.textureHelper.scaleTexture(texture, width, height);
    }

    private readTexturePixels(texture: WebGLRenderTarget): Float32Array {
        const pixelBuffer = new Float32Array(this.width * this.height * 4);

        try {
            this.renderer.readRenderTargetPixels(texture, 0, 0, this.width, this.height, pixelBuffer);
        } catch (error) {
            console.error("Error reading render target pixels:", error);
        }

        return pixelBuffer;
    }

    private initializeDepthArray(pixelBuffer: Float32Array): void {
        this.depth = new Float32Array(this.width * this.height);
        for (let i = 0; i < this.depth.length; i++) {
            const pixelIndex = 4 * i;
            const _r = pixelBuffer[pixelIndex];
            const _g = pixelBuffer[pixelIndex + 1];
            const _b = pixelBuffer[pixelIndex + 2];
            const alpha = pixelBuffer[pixelIndex + 3]; // Extracting alpha as depth

            this.depth[i] = alpha;
        }
    }

    // Get depth value for a 3D point
    getDepth(point: Vector3): number {
        const equirectangularCoord = this.getEquirectangularTexCoord(point);
        const pixelUV = this.getPixelUV(equirectangularCoord);

        const index = Math.floor(pixelUV.x) + Math.floor(pixelUV.y) * this.width;

        if (index >= 0 && index < this.depth.length) {
            return this.depth[index];
        }

        console.warn(`Depth index out of bounds: ${index}`);
        return 0; // Return a default value in case of out-of-bounds
    }

    // Convert a 3D point into equirectangular texture coordinates
    getEquirectangularTexCoord(vector: Vector3): Vector2 {
        const normalizedVector = vector.clone().normalize();
        const latitude = Math.acos(-normalizedVector.y); // Angle from the y-axis (up-down)
        const longitude = (Math.atan2(-normalizedVector.z, normalizedVector.x) + Math.PI) / (2 * Math.PI); // Angle from the x-axis (left-right)
        const u = longitude; // Horizontal coordinate (longitude)
        const v = latitude / Math.PI; // Vertical coordinate (latitude)

        return new Vector2(u, v);
    }

    private getPixelUV(equirectangularCoord: Vector2): Vector2 {
        const clampedX = (DepthManager.HORIZONTAL_OFFSET - equirectangularCoord.x + 1) % 1; // Wrap coordinates between 0 and 1
        const u = Math.max(0, Math.min(Math.floor(clampedX * this.width), this.width - 1));
        const v = Math.max(0, Math.min(Math.floor(equirectangularCoord.y * this.height), this.height - 1));

        return new Vector2(u, v);
    }

    dispose(): void {
        if (this.depth) {
            this.depth = new Float32Array();
        }
    }
}
